import { BaseModel, RecursivePartial } from '@models/base';
import { VzParam } from '@models/params/param';
import { getUuid } from '@models/utils/uuid';

export enum TaskFieldSystemType {
    Author          = 1,
    Assignee        = 2,
    Subj            = 3,
    Priority        = 4,
    ParentTask      = 5,
    Watchers        = 6,
    DueDate         = 7,
    Tag             = 8,
    PlannedHours    = 9,
    Link            = 10,
    Approver        = 11,
    Checklist       = 12,
    Text            = 13,
    ImagePreviews   = 14,
    Sprint          = 15,
}
export enum TaskLayoutBlockType {
    Container   = 1,
    System      = 2,
    Custom      = 3
}

export const TF_NAME: { [id in TaskFieldSystemType]?: string } = {
    [TaskFieldSystemType.Author]: 'Автор',
    [TaskFieldSystemType.PlannedHours]: 'Запланированное время',
    [TaskFieldSystemType.Watchers]: 'Наблюдатели',
    [TaskFieldSystemType.Assignee]: 'Ответственный',
    [TaskFieldSystemType.ImagePreviews]: 'Предпросмотр картинок',
    [TaskFieldSystemType.Priority]: 'Приоритет',
    [TaskFieldSystemType.Approver]: 'Проверяющие',
    [TaskFieldSystemType.ParentTask]: 'Родительская задача',
    [TaskFieldSystemType.Link]: 'Связи',
    [TaskFieldSystemType.DueDate]: 'Срок',
    [TaskFieldSystemType.Subj]: 'Тема',
    [TaskFieldSystemType.Tag]: 'Теги',
    [TaskFieldSystemType.Text]: 'Текст задачи',
    [TaskFieldSystemType.Checklist]: 'Чек-лист',
    [TaskFieldSystemType.Sprint]: 'Активная задача',
} as const;

export const TF_ICON: { [id in TaskFieldSystemType]?: string } = {
    [TaskFieldSystemType.Author]: 'vzi-person',
    [TaskFieldSystemType.PlannedHours]: 'vzi-history_toggle_off',
    [TaskFieldSystemType.Watchers]: 'vzi-eye',
    [TaskFieldSystemType.Assignee]: 'vzi-person-o',
    [TaskFieldSystemType.ImagePreviews]: 'vzi-file-image',
    [TaskFieldSystemType.Priority]: 'vzi-sort-asc',
    [TaskFieldSystemType.Approver]: 'vzi-checked',
    [TaskFieldSystemType.ParentTask]: 'vzi-level-up',
    [TaskFieldSystemType.Link]: 'vzi-link',
    [TaskFieldSystemType.DueDate]: 'vzi-calendar2',
    [TaskFieldSystemType.Subj]: 'vzi-notification',
    [TaskFieldSystemType.Tag]: 'vzi-tag-lines',
    [TaskFieldSystemType.Text]: 'vzi-file-text',
    [TaskFieldSystemType.Checklist]: 'vzi-completed',
    [TaskFieldSystemType.Sprint]: 'vzi-send',
} as const;


export const TaskFieldAlwaysShowIds: { [id in TaskFieldSystemType]?: boolean } = {
    [TaskFieldSystemType.Author]: true,
    [TaskFieldSystemType.Assignee]: true,
    [TaskFieldSystemType.Subj]: true,
} as const;

export const TaskCardAlwaysShowIds: { [id in TaskFieldSystemType]?: boolean } = {   // true - force YES, false - force NO
    [TaskFieldSystemType.Subj]: true,
    [TaskFieldSystemType.Priority]: true,
    [TaskFieldSystemType.Text]: false,
    [TaskFieldSystemType.ImagePreviews]: false,
    [TaskFieldSystemType.ParentTask]: false,
    [TaskFieldSystemType.Watchers]: false,
    [TaskFieldSystemType.Approver]: false,
    [TaskFieldSystemType.Checklist]: false,
    [TaskFieldSystemType.Sprint]: false,
} as const;

export const TF_CAN_BE_TEMPLATED: { [id in TaskFieldSystemType]?: boolean } = {
    [TaskFieldSystemType.Subj]: true,
    [TaskFieldSystemType.Priority]: true,
    [TaskFieldSystemType.Watchers]: true,
    [TaskFieldSystemType.Approver]: true,
    [TaskFieldSystemType.Checklist]: true,
    [TaskFieldSystemType.Assignee]: true,
    [TaskFieldSystemType.DueDate]: true,
    [TaskFieldSystemType.PlannedHours]: true,
    [TaskFieldSystemType.Tag]: true,
} as const;

export const TF_CAN_BE_REQUIRED: { [id in TaskFieldSystemType]?: boolean } = {
    [TaskFieldSystemType.Subj]: true,
    [TaskFieldSystemType.Author]: false,
    [TaskFieldSystemType.ParentTask]: false,
    [TaskFieldSystemType.ImagePreviews]: false,
    [TaskFieldSystemType.Text]: false,
} as const;

export class TaskLayoutBlock extends BaseModel {
    static Type = TaskLayoutBlockType;

    type!: TaskLayoutBlockType;

    // common
    name?: string;
    description?: string;
    flex?: number;

    // container type block
    children?: TaskLayoutBlock[];
    foldable?: boolean = false;
    folded?: boolean = false;
    horizontal?: boolean = false;
    childrenShift?: boolean = false;

    // field type blocks
    showInCard?: boolean = false;
    required?: boolean = false;
    icon?: string;
    color?: string;
    systemFieldId?: TaskFieldSystemType;         // for system predefined hardcoded fields
    fieldDef?: VzParam;                          // for custom defined fields

    _path?: string;
    _id?: string;
    _new?: boolean;

    constructor(json?: RecursivePartial<TaskLayoutBlock>) {
        super(json);
        this.parseTaskTypeDef(json || {});
    }

    parse(json: RecursivePartial<TaskLayoutBlock>): TaskLayoutBlock {
        super.parse(json);
        this.parseTaskTypeDef(json);
        return this;
    }

    private parseTaskTypeDef(json: RecursivePartial<TaskLayoutBlock>): void {
        TaskLayoutBlock.assignFields(this, json, [
            'type',
            'name',
            'description',
            'flex',
            'showInCard',
            'foldable',
            'folded',
            'horizontal',
            'childrenShift',
            'required',
            'icon',
            'color',
            'systemFieldId',
            '_path',
            '_new'
        ]);
        if (this.flex && this.flex < 0) {
            delete this.flex;
        }
        TaskLayoutBlock.assignClassFields(this, json, { fieldDef: VzParam });
        TaskLayoutBlock.assignClassArrays(this, json, { children: TaskLayoutBlock });
        setTaskBlockChildrenFlex(this);
        if (this.color == 'transparent') {
            this.color = undefined;
        }
        if (this.type == TaskLayoutBlockType.Container) {
            this._id = json._id || getUuid();
        }
        else if (this.type == TaskLayoutBlockType.Custom && this.fieldDef) {
            this._id = this.fieldDef.id || getUuid();
            if (this.required) {
                if (this.fieldDef.constraints) {
                    this.fieldDef.constraints.required = true;
                }
                else {
                    this.fieldDef.parse({ constraints: { required: true }});
                }
            }
            else {
                if (this.fieldDef.constraints) {
                    delete this.fieldDef.constraints.required;
                }
            }
            delete this.fieldDef.constraints?.errors;
            if (this.name && !this.fieldDef.name) {
                this.fieldDef.name = this.name;
            }
        }
        else {
            this._id = this.systemFieldId ? 'system_' + this.systemFieldId : getUuid();
        }
    }

}

export function setTaskBlockChildrenFlex(block: TaskLayoutBlock): TaskLayoutBlock {
    if (block.horizontal && block.children?.length) {
        while (block.children.length > 4) {
            block.children.pop();
        }
        if (block.children.length > 1) {
            const sum = block.children.map(x => x.flex || 1).reduce((s, v) => s + v, 0);
            if (sum > 12) {
                let left = sum - 12;
                for (let i = block.children.length - 1; i >= 0; i--) {
                    let v = block.children[i].flex || 1;
                    if (v > 1) {
                        const d = Math.min(v - 1, left);
                        left -= d;
                        v -= d;
                    }
                    block.children[i].flex = v;
                    if (left < 1) {
                        break;
                    }
                }
            }
            else if (sum < 12) {
                let left = 12 - sum;
                const k = 12 / sum;
                for (let i = block.children.length - 1; i >= 0; i--) {
                    let v = block.children[i].flex || 1;
                    const rv = Math.trunc(v * k);
                    left -= rv - v;
                    block.children[i].flex = rv;
                    if (left < 1) {
                        break;
                    }
                }
                if (left > 0) {
                    block.children[0].flex! += left;
                }
            }
        }
        else {
            block.children[0].flex = 1;
        }
    }
    return block;
}
